import React from 'react';
import styled, { keyframes } from 'styled-components';
import { APP_NOTICE_Z_INDEX } from '../styles/layers';
// TYPE-only: the view the overlay's hook returns, erased at build (the cards never reach the hook or Firebase).
import type { InAppMessagesView, MessageType, PriorityLevel } from '../hooks/inAppMessages/useInAppMessages';
import { IN_APP_MESSAGE_WORDS as W } from './inAppMessageWords';
// The message body is model output stored verbatim by send_platform_message_to_user
// (functions/src/index.ts writes content.html = the planner's message_content),
// so it renders through the same sanitizer the chat bubbles use — security
// remediation slice 4 (2026-09-15, docs/PLAN-security-remediation-20260915.md §2.6).
import { sanitizeHtmlInput } from './GlobalChatSystem/utils';

/*
 * TODAY'S IN-APP MESSAGE CARDS: the platform's messages to a signed-in person, above the chat's bar, drawn from the view
 * the overlay's hook returns (src/hooks/inAppMessages/useInAppMessages.ts: the listener, the delivery marks, the chat
 * hand-off, the sweep, every button and the inline form). InAppMessageOverlay.tsx draws them, or the new design's in their
 * place where the `in-app-messages` surface shows (D-92's light-only surfaces: the overlay, parts 1 and 2).
 */

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const OverlayContainer = styled.div`
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: calc((var(--prompt-bar-height, 56px)) + 12px + env(safe-area-inset-bottom, 0px));
  width: 90%;
  max-width: 800px;
  /* Above the chat in every mode (owner decision 2026-09-11): a platform
     notice parked behind the full-screen takeover never reaches someone
     who lives in the chat. Below the modal rung, so it never covers a
     dialog the user is answering. */
  z-index: ${APP_NOTICE_Z_INDEX};
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 40vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const MessageCard = styled.div<{ variant: MessageType; priority: PriorityLevel }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(0,0,0,0.08);
  background: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  animation: ${slideUp} 200ms ease-out;

  ${props => props.variant === 'info' ? 'border-left: 3px solid #3B82F6' : ''};
  ${props => props.variant === 'success' ? 'border-left: 3px solid #10B981' : ''};
  ${props => props.variant === 'warning' ? 'border-left: 3px solid #F59E0B' : ''};
  ${props => props.variant === 'error' ? 'border-left: 3px solid #EF4444' : ''};
  ${props => props.variant === 'alert' ? 'border-left: 3px solid #DC2626' : ''};
  ${props => props.variant === 'notification' ? 'border-left: 3px solid #6B7280' : ''};
  ${props => props.variant === 'system' ? 'border-left: 3px solid #111827' : ''};
`;

const Icon = styled.div`
  width: 20px;
  height: 20px;
  margin-top: 2px;
`;

const Content = styled.div`
  flex: 1;
  color: var(--brand-text);
  font-size: 0.95rem;
  line-height: 1.35;

  a { color: var(--brand-text); text-decoration: underline; }
  .mention { background: #F3F4F6; padding: 0 4px; border-radius: 4px; }
`;

const Actions = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const ActionBtn = styled.button<{ styleType: string }>`
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid #E5E7EB;
  background: ${p => p.styleType === 'primary' ? 'var(--brand-text)' : (p.styleType === 'destructive' ? '#FEE2E2' : '#fff')};
  color: ${p => p.styleType === 'primary' ? '#fff' : 'var(--brand-text)'};
  font-size: 0.85rem;
  cursor: pointer;
`;

const InlineForm = styled.form`
  display: flex;
  gap: 6px;
  margin-top: 6px;
  input, textarea { border: 1px solid #E5E7EB; border-radius: 6px; padding: 6px 8px; font-size: 0.9rem; }
  textarea { min-height: 56px; }
`;

/** Today's cards, drawn from the hook's view. */
export const InAppMessageCards: React.FC<{ view: InAppMessagesView }> = ({ view }) => {
  const { items, stackRef, activeForm, formValue, setFormValue } = view;
  return (
    <OverlayContainer>
      <Stack ref={stackRef}>
        {items.map((m) => (
          <MessageCard key={m.message_id} variant={m.message_type} priority={m.priority_level}>
            <Icon aria-hidden="true">{/* icon may be swapped for lucide icon if desired */}
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.75">
                <circle cx="12" cy="12" r="10" />
              </svg>
            </Icon>
            <Content
              onMouseEnter={() => view.onView(m)}
              dangerouslySetInnerHTML={{ __html: sanitizeHtmlInput(view.messageBody(m)) }}
            />
            <Actions>
              {(m.action_buttons || []).map((b, idx) => (
                <ActionBtn key={idx} styleType={b.button_style} onClick={() => view.onAction(m, b)}>
                  {b.button_text}
                </ActionBtn>
              ))}
            </Actions>
            {activeForm && activeForm.messageId === m.message_id && (
              <InlineForm onSubmit={view.onSubmitForm}>
                <textarea value={formValue} onChange={(ev) => setFormValue(ev.target.value)} placeholder={W.formPlaceholder} />
                <ActionBtn styleType={'primary'} as="button" type="submit">{W.submit}</ActionBtn>
                <ActionBtn styleType={'secondary'} as="button" type="button" onClick={view.onCancelForm}>{W.cancel}</ActionBtn>
              </InlineForm>
            )}
          </MessageCard>
        ))}
      </Stack>
    </OverlayContainer>
  );
};

export default InAppMessageCards;
